import { useEffect, useState } from "react";
import {
  apiErrorMessage,
  createOtherTaskNote,
  deleteOtherTaskNote,
  getOtherTaskNotes,
  setOtherTaskRepeats,
  updateOtherTaskNote,
} from "../../../api";
import OwnerBubble from "../OwnerBubble";
import { REPEAT_LABELS, isOverdue } from "../bookkeeping";

// The Bookkeeping slide-out: a short overview of one period and its notes.
// A note can be a to-do ("Waiting on Nov sales report") with a tick box;
// ticking records who and when.

const label = {
  fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase",
  color: "#676879", fontWeight: 600, marginBottom: 10,
};

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

export default function BookkeepingPanel({
  task, statusOptions = [], otherOpenPeriods = [], onSelectTask,
  onPatch, onEditOwners, onClose, onNotesChanged, onRepeatsChanged,
}) {
  const [nameDraft, setNameDraft] = useState(task.name || "");
  useEffect(() => { setNameDraft(task.name || ""); }, [task.name]);
  const saveName = () => {
    const next = nameDraft.trim();
    if (!next) { setNameDraft(task.name || ""); return; }
    if (next !== task.name) onPatch(task, { name: next });
  };
  const monthOf = (iso) => (iso || "").slice(0, 7);
  const savePeriod = (which, month) => {
    if (!month) {
      if (which === "start") onPatch(task, { period_start: null, period_end: null });
      return;
    }
    const [y, m] = month.split("-").map(Number);
    if (which === "start") {
      const payload = { period_start: `${month}-01` };
      if (!task.period_end || task.period_end < payload.period_start) {
        payload.period_end = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
      }
      onPatch(task, payload);
    } else {
      onPatch(task, { period_end: `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}` });
    }
  };
  const [repeatsSaving, setRepeatsSaving] = useState(false);
  const [repeatsNote, setRepeatsNote] = useState("");
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [draft, setDraft] = useState("");
  const [draftTodo, setDraftTodo] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    setLoadingNotes(true);
    setError("");
    getOtherTaskNotes(task.id)
      .then((rows) => { if (live) setNotes(rows || []); })
      .catch((e) => { if (live) setError(apiErrorMessage(e, "Could not load notes.")); })
      .finally(() => { if (live) setLoadingNotes(false); });
    return () => { live = false; };
  }, [task.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    try {
      const created = await createOtherTaskNote({ task: task.id, text, is_todo: draftTodo });
      setNotes((prev) => [created, ...prev]);
      setDraft("");
      setDraftTodo(false);
      onNotesChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not add the note."));
    }
  };

  const toggleDone = async (note) => {
    try {
      const updated = await updateOtherTaskNote(note.id, { is_done: !note.is_done });
      setNotes((prev) => prev.map((n) => (n.id === note.id ? updated : n)));
      onNotesChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not update the note."));
    }
  };

  const removeNote = async (note) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteOtherTaskNote(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      onNotesChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not delete the note."));
    }
  };

  const status = statusOptions.find((s) => String(s.id) === String(task.status));
  const changeRepeats = async (next) => {
    const current = task.recurrence || "none";
    if (next === current) return;
    if (next === "none" && task.template &&
        !window.confirm("Stop repeating? Open periods stay; no new ones will be made.")) return;
    setRepeatsSaving(true);
    setError("");
    try {
      await setOtherTaskRepeats(task.id, next);
      setRepeatsNote(next === "none"
        ? "No more periods will be made."
        : task.template
          ? `Future periods are ${REPEAT_LABELS[next].toLowerCase()}, starting after the latest one.`
          : `Now ${REPEAT_LABELS[next].toLowerCase()}; this is its first period.`);
      await onRepeatsChanged?.();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not change how often it repeats."));
    } finally {
      setRepeatsSaving(false);
    }
  };
  const overdue = isOverdue(task);

  return (
    <aside
      aria-label="Task details"
      style={{
        width: 380, flexShrink: 0, borderLeft: "1px solid #e6e9ef",
        background: "#ffffff", display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 24px rgba(0,0,0,0.06)", overflow: "hidden",
      }}
    >
      <div style={{
        padding: "16px 18px", borderBottom: "1px solid #e6e9ef",
        display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#323338" }}>
            {task.client_name || "No client"}
          </div>
          <div style={{ color: "#676879", marginTop: 2, fontSize: 13 }}>
            {task.name}{task.period_label ? ` · ${task.period_label}` : ""}
          </div>
        </div>
        <button
          type="button" onClick={onClose} aria-label="Close"
          style={{ border: "none", background: "none", fontSize: 18, color: "#9699a6", cursor: "pointer" }}
        >✕</button>
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        <section style={{ padding: "14px 18px", borderBottom: "1px solid #f1f3f6" }}>
          <div style={label}>Overview</div>
          <dl style={{
            display: "grid", gridTemplateColumns: "88px 1fr", rowGap: 10,
            margin: 0, fontSize: 13, alignItems: "center",
          }}>
            <dt style={{ color: "#676879" }}>Work item</dt>
            <dd style={{ margin: 0 }}>
              <input
                type="text" aria-label="Work item"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") { e.stopPropagation(); setNameDraft(task.name || ""); }
                }}
                style={{
                  width: "100%", boxSizing: "border-box", border: "1px solid #d0d4e4",
                  borderRadius: 4, padding: "4px 6px", fontSize: 13, color: "#323338",
                }}
              />
            </dd>

            <dt style={{ color: "#676879" }}>Period</dt>
            <dd style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="month" aria-label="Period from"
                value={monthOf(task.period_start)}
                onChange={(e) => savePeriod("start", e.target.value)}
                style={{ border: "1px solid #d0d4e4", borderRadius: 4, padding: "3px 4px", fontSize: 12, width: 128 }}
              />
              <span style={{ color: "#676879" }}>to</span>
              <input
                type="month" aria-label="Period to"
                value={monthOf(task.period_end)}
                disabled={!task.period_start}
                onChange={(e) => savePeriod("end", e.target.value)}
                style={{ border: "1px solid #d0d4e4", borderRadius: 4, padding: "3px 4px", fontSize: 12, width: 128 }}
              />
            </dd>

            <dt style={{ color: "#676879" }}>Status</dt>
            <dd style={{ margin: 0 }}>
              <select
                aria-label="Status"
                value={task.status ?? ""}
                onChange={(e) => onPatch(task, { status: e.target.value ? Number(e.target.value) : null })}
                style={{
                  border: "none", borderRadius: 999, padding: "3px 10px", fontSize: 12,
                  background: status?.color || "#e6e9ef",
                  color: status?.color ? "#ffffff" : "#323338", cursor: "pointer",
                }}
              >
                <option value="">No status</option>
                {statusOptions.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </dd>

            <dt style={{ color: "#676879" }}>Due</dt>
            <dd style={{ margin: 0 }}>
              <input
                type="date" aria-label="Due date"
                value={task.due_date || ""}
                onChange={(e) => onPatch(task, { due_date: e.target.value || null })}
                style={{
                  border: "1px solid #d0d4e4", borderRadius: 4, padding: "3px 6px",
                  fontSize: 13, color: overdue ? "#d83a52" : "#323338",
                }}
              />
            </dd>

            <dt style={{ color: "#676879" }}>Owner</dt>
            <dd style={{ margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
              {(task.assignments || []).map((a) => (
                <span key={a.id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <OwnerBubble owner={a.employee_name || ""} size={22} />
                  <span>{a.employee_name}</span>
                </span>
              ))}
              <button
                type="button" onClick={() => onEditOwners(task)}
                style={{ border: "none", background: "none", color: "#0073ea", cursor: "pointer", fontSize: 12 }}
              >
                {(task.assignments || []).length ? "Change" : "Assign"}
              </button>
            </dd>

            <dt style={{ color: "#676879" }}>Repeats</dt>
            <dd style={{ margin: 0 }}>
              <select
                aria-label="Repeats"
                value={task.recurrence || "none"}
                disabled={repeatsSaving}
                onChange={(e) => changeRepeats(e.target.value)}
                style={{
                  border: "1px solid #d0d4e4", borderRadius: 4, padding: "3px 6px",
                  fontSize: 13, color: "#323338", background: "#fff",
                }}
              >
                <option value="monthly">{REPEAT_LABELS.monthly}</option>
                <option value="quarterly">{REPEAT_LABELS.quarterly}</option>
                <option value="annual">{REPEAT_LABELS.annual}</option>
                <option value="none">Does not repeat</option>
              </select>
              {repeatsNote && (
                <div style={{ fontSize: 11, color: "#676879", marginTop: 4 }}>{repeatsNote}</div>
              )}
            </dd>

            {otherOpenPeriods.length > 0 && (
              <>
                <dt style={{ color: "#676879" }}>Also open</dt>
                <dd style={{ margin: 0, display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {otherOpenPeriods.map((p) => (
                    <button
                      key={p.id} type="button" onClick={() => onSelectTask(p)}
                      style={{
                        border: "none", background: "none", padding: 0,
                        color: "#0073ea", textDecoration: "underline", cursor: "pointer", fontSize: 13,
                      }}
                    >
                      {p.period_label || p.name}
                    </button>
                  ))}
                </dd>
              </>
            )}
          </dl>
        </section>

        <section style={{ padding: "14px 18px" }}>
          <div style={label}>Notes</div>
          {error && (
            <div role="alert" style={{ color: "#d83a52", fontSize: 12, marginBottom: 8 }}>{error}</div>
          )}
          {loadingNotes ? (
            <div style={{ color: "#9699a6", fontSize: 13 }}>Loading…</div>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {notes.length === 0 && !(task.notes || "").trim() && (
                <li style={{ color: "#9699a6", fontSize: 13, padding: "4px 0 8px" }}>No notes yet.</li>
              )}
              {notes.map((n, i) => (
                <li
                  key={n.id}
                  className="bk-note"
                  style={{
                    display: "flex", gap: 10, padding: "8px 0", alignItems: "flex-start",
                    borderTop: i ? "1px solid #f3f4f6" : "none",
                  }}
                >
                  {n.is_todo ? (
                    <input
                      type="checkbox" checked={n.is_done} onChange={() => toggleDone(n)}
                      aria-label={n.is_done ? "Mark not done" : "Mark done"}
                      style={{ marginTop: 3, cursor: "pointer" }}
                    />
                  ) : (
                    <span style={{ width: 13, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, whiteSpace: "pre-wrap", wordBreak: "break-word",
                      color: n.is_done ? "#9699a6" : "#323338",
                      textDecoration: n.is_done ? "line-through" : "none",
                    }}>{n.text}</div>
                    <div style={{ fontSize: 11, color: "#9699a6", marginTop: 2 }}>
                      {[n.created_by_name, shortDate(n.created_at)].filter(Boolean).join(" · ")}
                      {n.is_done && ` · done ${shortDate(n.done_at)}${n.done_by_name ? ` by ${n.done_by_name}` : ""}`}
                    </div>
                  </div>
                  <button
                    type="button" onClick={() => removeNote(n)} aria-label="Delete note"
                    title="Delete note"
                    style={{ border: "none", background: "none", color: "#c3c6d4", cursor: "pointer", fontSize: 13 }}
                  >✕</button>
                </li>
              ))}
              {(task.notes || "").trim() && (
                <li style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: notes.length ? "1px solid #f3f4f6" : "none" }}>
                  <span style={{ width: 13, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "#323338" }}>{task.notes}</div>
                    <div style={{ fontSize: 11, color: "#9699a6", marginTop: 2 }}>Earlier note</div>
                  </div>
                </li>
              )}
            </ul>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); addNote(); }}
            style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}
          >
            <input
              type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a note…" aria-label="New note"
              style={{ flex: 1, border: "1px solid #d0d4e4", borderRadius: 6, padding: "7px 9px", fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: "#676879", whiteSpace: "nowrap", display: "flex", gap: 4, alignItems: "center" }}>
              <input type="checkbox" checked={draftTodo} onChange={(e) => setDraftTodo(e.target.checked)} />
              to-do
            </label>
            <button
              type="submit" disabled={!draft.trim()}
              style={{
                border: "none", borderRadius: 6, padding: "7px 12px", fontSize: 13,
                background: draft.trim() ? "#00854d" : "#c3c6d4", color: "#fff",
                cursor: draft.trim() ? "pointer" : "default",
              }}
            >Add</button>
          </form>
        </section>
      </div>
    </aside>
  );
}

